import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Landing page CMI redirects back to (via the backend's web_return_url proxy).
 * URL: /payment-result?status=success|failed&payment_id=...
 */
const PaymentResultPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const paymentId = searchParams.get('payment_id');

  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!paymentId) {
      const id = window.setTimeout(() => setLoading(false), 0);
      return () => window.clearTimeout(id);
    }
    let cancelled = false;
    const reconcile = async () => {
      try {
        // POST to reconcile the payment server-side. This is idempotent and
        // mirrors the logic that previously ran on the backend okUrl redirect.
        // We always POST (even on failed/cancelled) so we get the latest
        // payment status back; the backend only flips pending→completed when
        // status=success.
        const reconcileStatus = status || 'success';
        const response = await axios.post(
          `${API}/payments/${paymentId}/reconcile`,
          null,
          { params: { status: reconcileStatus } }
        );
        if (cancelled) return;
        setPayment(response.data);
      } catch {
        // Reconcile failed → fall back to plain status lookup so we can still
        // render something useful.
        try {
          const response = await axios.get(`${API}/payments/${paymentId}/status`);
          if (!cancelled) setPayment(response.data);
        } catch {
          // Non-blocking — we still have the status from the query string
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    reconcile();
    return () => {
      cancelled = true;
    };
  }, [paymentId, status]);

  const isSuccess = status === 'success' || payment?.status === 'completed';

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <Navbar />
      <section className="flex-1 flex items-center justify-center px-4 pt-24 pb-12">
        <div className="max-w-md w-full bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-3xl p-8 md:p-10 text-center">
          {loading ? (
            <>
              <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Checking payment status...</p>
            </>
          ) : isSuccess ? (
            <>
              <div className="w-16 h-16 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-emerald-500" />
              </div>
              <h1
                className="text-3xl font-bold text-white mb-3"
                data-testid="payment-result-heading"
              >
                Payment Successful!
              </h1>
              {payment && (
                <p className="text-gray-400 mb-1">
                  Your payment of{' '}
                  <span className="text-amber-500 font-semibold">
                    MAD {payment.amount?.toFixed(2)}
                  </span>{' '}
                  has been processed.
                </p>
              )}
              <p className="text-gray-500 text-sm mb-8">
                A confirmation will be sent to your email. The transporter has been notified.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-3 rounded-full"
                data-testid="back-to-dashboard-btn"
              >
                Back to My Dashboard
              </button>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1
                className="text-3xl font-bold text-white mb-3"
                data-testid="payment-result-heading"
              >
                Payment Failed
              </h1>
              <p className="text-gray-400 mb-8">
                Your payment could not be processed. Please try again or use a different card.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => navigate(-1)}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-3 rounded-full"
                  data-testid="retry-payment-btn"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-white/5 border border-white/10 text-white font-semibold py-3 rounded-full hover:bg-white/10"
                >
                  Back to Dashboard
                </button>
              </div>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PaymentResultPage;
